import * as Sentry from "@sentry/react";
import type { Metric } from "web-vitals";

export type MemorySample = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};

type VitalEntry = {
  value: number;
  rating: Metric["rating"];
};

export type PerfSnapshot = {
  vitals: Record<string, VitalEntry>;
  memory: MemorySample | null;
  memoryAvailable: boolean;
};

export type MemoryReader = {
  read: () => MemorySample | null;
};

type PerfMetricsMonitorOptions = {
  memoryReader: MemoryReader;
  pollIntervalMs?: number;
  sentryReportIntervalMs?: number;
  getVisibilityState?: () => DocumentVisibilityState;
};

const POLL_INTERVAL_MS = 2000;
const SENTRY_REPORT_INTERVAL_MS = 30_000;

export class PerfMetricsMonitor {
  private vitals: Record<string, VitalEntry> = {};
  private memory: MemorySample | null = null;
  private memoryAvailable = true;
  private listeners: Array<(snapshot: PerfSnapshot) => void> = [];
  private isMonitoring = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private sentryIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly memoryReader: MemoryReader;
  private readonly pollIntervalMs: number;
  private readonly sentryReportIntervalMs: number;
  private readonly getVisibilityState: () => DocumentVisibilityState;

  constructor(options: PerfMetricsMonitorOptions) {
    this.memoryReader = options.memoryReader;
    this.pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS;
    this.sentryReportIntervalMs =
      options.sentryReportIntervalMs ?? SENTRY_REPORT_INTERVAL_MS;
    this.getVisibilityState =
      options.getVisibilityState ??
      (() =>
        typeof document !== "undefined" ? document.visibilityState : "visible");
  }

  public start(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    this.intervalId = setInterval(() => {
      this.pollMemory();
    }, this.pollIntervalMs);

    this.sentryIntervalId = setInterval(() => {
      this.reportMemoryToSentry();
    }, this.sentryReportIntervalMs);
  }

  public stop(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.sentryIntervalId !== null) {
      clearInterval(this.sentryIntervalId);
      this.sentryIntervalId = null;
    }
  }

  public reportVital(metric: Metric): void {
    this.vitals[metric.name] = {
      value: metric.value,
      rating: metric.rating,
    };

    this.notifyListeners();
  }

  public getVitals(): Record<string, VitalEntry> {
    return { ...this.vitals };
  }

  public getMemory(): MemorySample | null {
    return this.memory;
  }

  public isMemoryAvailable(): boolean {
    return this.memoryAvailable;
  }

  public getSnapshot(): PerfSnapshot {
    return {
      vitals: this.getVitals(),
      memory: this.memory,
      memoryAvailable: this.memoryAvailable,
    };
  }

  public addListener(listener: (snapshot: PerfSnapshot) => void): () => void {
    this.listeners.push(listener);

    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  public clear(): void {
    this.vitals = {};
    this.memory = null;
    this.notifyListeners();
  }

  private pollMemory(): void {
    if (this.getVisibilityState() === "hidden") {
      return;
    }

    const sample = this.memoryReader.read();

    if (sample === null) {
      this.memoryAvailable = false;
      return;
    }

    this.memory = sample;
    this.notifyListeners();
  }

  private reportMemoryToSentry(): void {
    if (this.getVisibilityState() === "hidden" || this.memory === null) {
      return;
    }

    const sample = this.memory;
    Sentry.metrics.gauge(
      "memory.js_heap_used",
      sample.usedJSHeapSize / 1024 / 1024,
      { unit: "megabyte" }
    );
    Sentry.metrics.gauge(
      "memory.js_heap_total",
      sample.totalJSHeapSize / 1024 / 1024,
      { unit: "megabyte" }
    );
    Sentry.metrics.gauge(
      "memory.js_heap_limit",
      sample.jsHeapSizeLimit / 1024 / 1024,
      { unit: "megabyte" }
    );
  }

  private notifyListeners(): void {
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}

const browserMemoryReader: MemoryReader = {
  read: () => {
    const mem = (performance as Performance & { memory?: MemorySample }).memory;

    if (!mem) {
      return null;
    }

    return {
      usedJSHeapSize: mem.usedJSHeapSize,
      totalJSHeapSize: mem.totalJSHeapSize,
      jsHeapSizeLimit: mem.jsHeapSizeLimit,
    };
  },
};

export const perfMetricsMonitor = new PerfMetricsMonitor({
  memoryReader: browserMemoryReader,
});
